import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/ScrollReveal.css';

gsap.registerPlugin(ScrollTrigger);

const SELECTORS = [
  'section',
  '.glass-card',
  '.glass-panel',
  '.card',
  '.panel',
  '.bg-surface',
  '.blue-radial-bg',
  '.enterprise-shadow',
  '.enterprise-border',
  '.grid > div',
  '.list > li',
  '.stat, .stats > *'
].join(',');

const EXCLUDE = 'header, nav, .skip-reveal, [data-reveal="off"]';

export default function GlobalScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    // Allow DOM to paint after route change
    const raf = requestAnimationFrame(() => {
      try {
        // Clear existing triggers to avoid duplicates when navigating
        ScrollTrigger.getAll().forEach(t => t.kill());

        const nodes = Array.from(document.querySelectorAll(SELECTORS))
          .filter(el => !el.closest(EXCLUDE));

        nodes.forEach((el, index) => {
          if (!el || el.dataset.srInit === '1') return;
          el.dataset.srInit = '1';

          const isHeading = el.matches('h1, h2, h3, h4');

          // Optional word-based animation for plain-text headings
          let wordSpans = null;
          if (isHeading && el.childElementCount === 0) {
            const text = el.textContent || '';
            const frag = document.createDocumentFragment();
            text.split(/(\s+)/).forEach((part, i) => {
              if (/^\s+$/.test(part)) {
                frag.appendChild(document.createTextNode(part));
              } else {
                const span = document.createElement('span');
                span.className = 'sr-word';
                span.textContent = part;
                frag.appendChild(span);
              }
            });
            el.textContent = '';
            el.appendChild(frag);
            wordSpans = el.querySelectorAll('.sr-word');
          }

          const tl = gsap.timeline({ paused: true });
          tl.fromTo(
            el,
            { autoAlpha: 0, y: 24, filter: 'blur(8px)' },
            { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power3.out', clearProps: 'filter,transform' }
          );

          if (wordSpans && wordSpans.length > 0) {
            tl.fromTo(
              wordSpans,
              { autoAlpha: 0.2, y: '0.35em' },
              { autoAlpha: 1, y: '0em', duration: 0.65, stagger: 0.04, ease: 'power2.out' },
              0
            );
          }

          ScrollTrigger.create({
            trigger: el,
            start: 'top 85%',
            once: true, // play only once
            onEnter: () => tl.play(), // only on downward enter
            // no onEnterBack to avoid playing when scrolling up
          });
        });

        // Refresh to position calculations after setup
        ScrollTrigger.refresh();
      } catch {
        /* noop */
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  return null;
}


